<script setup lang="ts">
import {computed, type PropType, ref, watch} from "vue";
import Translator from "@/utils/common/Translator";
import AddDocumentIcon from "@/components/icons/toastIcons/AddDocumentIcon.vue";
import QuestionIcon from "@/components/icons/toastIcons/QuestionIcon.vue";
import InfoIcon from "@/components/icons/toastIcons/InfoIcon.vue";
import {useNotificationsStore} from "@/stores/ToastDataStore";
import NamaCommon from "@/utils/common/NamaCommon";
import NamaURLS from "@/utils/constants/NamaURLS";
import {StringUtils} from "@/utils/common/StringUtils";
import type {AxiosRequestConfig} from "axios";
import dayjs from "@/utils/dayjs";
import ObjectChecker from "@/utils/common/ObjectChecker";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import TopPanelSearchUtils from "@/utils/common/TopPanelSearchUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import CloseIcon from "@/components/icons/toastIcons/CloseIcon.vue";

let current = ref(1);
let notificationsStore = useNotificationsStore();
let props = defineProps({
  errors: {
    required: true,
    type: Object as PropType<namaTypes.NaMaError[]>,
  }
});

function fetchCurrentError(): namaTypes.NaMaError {
  return props.errors[current.value - 1];
}

function showErrorOriginalMsg() {
  if (fetchCurrentError().rawMessage)
    notificationsStore.pushNotificationText(fetchCurrentError().rawMessage!);
}

async function showErrorMsgHelp() {
  let namaError = fetchCurrentError();
  let params: AxiosRequestConfig = {
    params: {
      request: JSON.stringify({
        errorMsg: namaError.rawMessage
      })
    }
  };
  let data = await NamaCommon.namaFetch(NamaURLS.SearchForHelpMsgs, params)
  if (!data.helps && !data.systemHelps)
    return;
  let helpsContent = "";
  if (ObjectChecker.isNotEmptyOrNull(data.helps)) {
    helpsContent += StringUtils.toCSVLineWithSep("<br/><br/>", data.helps);
  }
  if (ObjectChecker.isNotEmptyOrNull(data.systemHelps)) {
    helpsContent += "<hr/>";
    helpsContent += StringUtils.toCSVLineWithSep("<br/><br/>", data.systemHelps);
  }
  if (helpsContent)
    notificationsStore.pushNotificationText(helpsContent);
}

const relatedToURL = computed(() => {
  let error = fetchCurrentError();
  if (!error)
    return undefined;
  if (error.alwaysShowRelatedTo || useNamaFlagsStore().serverLogActive) {
    if (!error.relatedToOptionType)
      return undefined;
    if (error.relatedToOptionType === "URL")
      return error.relatedToOption;
    let place = PlaceUtils.editPlace(error.relatedToOptionType, error.relatedToOptionTypeId)!;
    place.focusOnField = error.relatedToOption;
    return PlaceUtils.linkToPlace(place);
  }
  return undefined;
})
const relatedToTitle = computed(() => {
  let error = fetchCurrentError();
  if (!error)
    return undefined;
  if (error.relatedToOptionType === "URL")
    return Translator.translateId(error.relatedToOptionCode)
  return Translator.translateId(error.relatedToOptionType) + " - " + Translator.translate(error.relatedToOptionCode) + " - " + Translator.translateId(error.relatedToOption)
})

async function exportToExcel() {
  let data: any[] = [];
  data.push(["Error", "Field ID", "Field Name", "Line Number"]);
  let errors = props.errors.map(function (e) {
    let row: number | undefined | null = e.row;
    if (row != null && row >= 0)
      row += 1;
    else
      row = null;
    return [Translator.translateNaMaText(e.message), "", Translator.translateId(e.ownerId ?? ""), String(row ?? "")];
  });
  data.push(...errors);
  let date = dayjs(new Date()).format("YYYYMMDDHHmm");
  let params: AxiosRequestConfig = {
    params: {
      fileName: "Errors" + date + ".xlsx",
      inBody: true
    },
    data: data
  };
  let result = await NamaCommon.namaFetch(NamaURLS.ExportToExcel, params);
  window.open(result.data, "_blank");
}

function canShowFocusToFieldBtn(): boolean {
  const error = fetchCurrentError();
  return !!error.ownerId
}

async function onFieldToFocusBtnClick() {
  const error = fetchCurrentError()
  const fieldId = error.ownerId
  if (!fieldId)
    return;
  const fields = TopPanelSearchUtils.searchForFields(fieldId);
  const fieldToFocus = fields.find(f => f.id === fieldId);
  if (!fieldToFocus)
    return;
  const agRowIndex = error.row;
  if (ObjectChecker.isNotNullOrUndefined(agRowIndex))
    fieldToFocus.record.extraData.agRowIndex = agRowIndex
  useNamaFlagsStore().setFieldToFocusOptions({field: fieldToFocus})
}

watch(() => props.errors.length, (newLength) => {
  if (newLength > 0)
    current.value = newLength
}, {immediate: true})
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-gap-4">
    <div class="tw-flex tw-justify-between tw-items-baseline tw-w-full tw-gap-2">
      <div class="tw-font-bold tw-text-lg  tw-text-white tw-max-h-[50vh] tw-overflow-auto"
           v-html="current + '. ' + Translator.translateNaMaText(errors[current - 1]?.message)"/>
      <q-avatar square :title="Translator.translateId('close')" @click="$emit('close-toast')" class="tw-size-4 tw-cursor-pointer">
        <CloseIcon class="tw-w-[14px] hover:tw-fill-gray-100 tw-divide-opacity-80"/>
      </q-avatar>
    </div>
    <div class="tw-self-end tw-flex tw-w-full" v-if="errors.length>=1">
      <q-pagination v-model="current" :max="errors.length" :max-pages="1" :ellipses="false" input
                    :boundary-numbers="false" direction-links class="error-toast-page tw-grow" input-class="error-toast-input"/>
      <div class="tw-shrink-0 tw-flex tw-gap-3 tw-justify-between tw-items-center">
        <q-avatar v-if="canShowFocusToFieldBtn()" square :title="Translator.translateId('goToRelatedField')" @click.stop="onFieldToFocusBtnClick"
                  class="tw-size-5 tw-cursor-pointer">
          <NamaIcon default-icon="nm-focus" class="active-white tw-size-5"/>
        </q-avatar>
        <q-avatar square :title="Translator.translateId('showHelpMessage')" @click.stop="showErrorMsgHelp" class="tw-size-5 tw-cursor-pointer">
          <QuestionIcon class="tw-w-[18px]"/>
        </q-avatar>
        <q-avatar square :title="Translator.translateId('exportcsv')" @click.stop="exportToExcel" class="tw-size-5 tw-cursor-pointer">
          <AddDocumentIcon class="tw-w-[18px]"/>
        </q-avatar>
        <q-avatar square :title="Translator.translateId('showOriginalMsg')" @click.stop="showErrorOriginalMsg" class="tw-size-5 tw-cursor-pointer">
          <InfoIcon class="tw-w-[18px]"/>
        </q-avatar>
      </div>
    </div>
    <a v-if="relatedToURL" :href="relatedToURL" v-text="relatedToTitle"
       class="tw-text-neutral-100 tw-underline tw-text-lg"/>
  </div>
</template>

<style>

</style>
